import { useState } from "react";
import { X, Save, Loader2, Plus, Trash2, Search, UserCheck } from "lucide-react";
import { api, euro } from "@/lib/api";
import { toast } from "sonner";
import { GermanDatePicker } from "@/components/ui/date-picker";

const inputCls = "w-full bg-[#141414] border border-white/10 text-white text-sm p-2.5 focus:border-[#ff0033] outline-none placeholder:text-neutral-600";
const lbl = "text-neutral-500 text-[10px] uppercase tracking-widest font-mono-lab";

const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const emptyLine = () => ({ name: "", quantity: 1, gross_unit: "", vat_rate: "19", occurrence: "" });

const lineTotals = (l) => {
  const gross = Math.round(num(l.gross_unit) * (num(l.quantity) || 1) * 100) / 100;
  const rate = num(l.vat_rate);
  const net = Math.round((gross / (1 + rate / 100)) * 100) / 100;
  return { gross, net, vat: Math.round((gross - net) * 100) / 100, rate };
};

const initialLines = (inv) => {
  if (!inv) return [emptyLine()];
  if (inv.lines?.length) {
    return inv.lines.map((l) => ({
      name: l.name || "", quantity: l.quantity ?? 1, gross_unit: String(l.gross_unit ?? ""),
      vat_rate: String(Math.round(l.vat_rate ?? 19)), occurrence: l.occurrence || "",
    }));
  }
  return [{
    name: inv.line?.name || "", quantity: inv.line?.quantity ?? 1,
    gross_unit: String(inv.line?.gross_unit ?? ""),
    vat_rate: String(Math.round(inv.amounts?.vat_rate ?? 19)), occurrence: inv.line?.occurrence || "",
  }];
};

export const InvoiceEditor = ({ inv, onClose, onSaved }) => {
  const isNew = !inv;
  const [c, setC] = useState({
    first_name: inv?.customer?.first_name || "", last_name: inv?.customer?.last_name || "",
    company: inv?.customer?.company || "", street: inv?.customer?.street || "",
    postcode: inv?.customer?.postcode || "", city: inv?.customer?.city || "",
    country: inv?.customer?.country || "Deutschland", email: inv?.customer?.email || "",
    vat_id: inv?.customer?.vat_id || "", phone: inv?.customer?.phone || "",
  });
  const [issueDate, setIssueDate] = useState((inv?.issue_date || new Date().toISOString()).slice(0, 10));
  const [serviceDate, setServiceDate] = useState((inv?.service_date || "").slice(0, 10));
  const [lines, setLines] = useState(initialLines(inv));
  const [vatNote, setVatNote] = useState(inv?.vat_note || "");
  const [thankYou, setThankYou] = useState(inv?.thank_you || "");
  const [emailMessage, setEmailMessage] = useState(inv?.email_message || "");
  const [invoiceKind, setInvoiceKind] = useState(inv?.invoice_kind || "golftraining");
  const [sendAfter, setSendAfter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);

  const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines([...lines, emptyLine()]);
  const delLine = (i) => setLines(lines.length > 1 ? lines.filter((_, idx) => idx !== i) : lines);

  const totals = lines.reduce((s, l) => {
    const t = lineTotals(l);
    return { gross: s.gross + t.gross, net: s.net + t.net, vat: s.vat + t.vat };
  }, { gross: 0, net: 0, vat: 0 });
  const hasZeroVat = lines.some((l) => num(l.vat_rate) === 0);

  const search = async (value) => {
    setQ(value);
    if (value.trim().length < 2) { setHits([]); return; }
    try { const r = await api.get("/admin/customers", { params: { q: value.trim() } }); setHits(r.data.slice(0, 6)); }
    catch { setHits([]); }
  };
  const pick = (cu) => {
    setC({
      first_name: cu.first_name || "", last_name: cu.last_name || "", company: cu.company || "",
      street: cu.street || "", postcode: cu.postcode || "", city: cu.city || "",
      country: cu.country || "Deutschland", email: cu.email || "", vat_id: cu.vat_id || "", phone: cu.phone || "",
    });
    setHits([]); setQ("");
    toast.success("Kundendaten übernommen");
  };

  const save = async () => {
    const payloadLines = lines
      .filter((l) => l.name.trim() || num(l.gross_unit))
      .map((l) => ({ name: l.name.trim(), quantity: num(l.quantity) || 1, gross_unit: num(l.gross_unit), vat_rate: num(l.vat_rate), occurrence: l.occurrence || "" }));
    if (!payloadLines.length) { toast.error("Mindestens eine Rechnungsposition eintragen"); return; }
    if (payloadLines.some((l) => !l.name)) { toast.error("Jede Position braucht eine Beschreibung"); return; }
    if (isNew && payloadLines.some((l) => l.gross_unit <= 0)) { toast.error("Einzelpreis muss größer 0 sein"); return; }
    if (isNew && !c.last_name.trim() && !c.company.trim()) { toast.error("Name oder Firma des Empfängers fehlt"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/admin/invoices/manual", {
          customer: c, issue_date: issueDate, service_date: serviceDate, lines: payloadLines,
          vat_note: hasZeroVat ? vatNote : "", thank_you: thankYou, email_message: emailMessage,
          invoice_kind: invoiceKind, send: sendAfter,
        });
        toast.success(`Rechnung ${r.data.invoice_number} erstellt (${euro(r.data.gross)})${sendAfter ? " und per E-Mail versendet" : ""}`);
      } else {
        const r = await api.put(`/admin/invoices/${inv.id}`, {
          customer: c, issue_date: issueDate, service_date: serviceDate, lines: payloadLines,
          vat_note: hasZeroVat ? vatNote : "", thank_you: thankYou, email_message: emailMessage,
          invoice_kind: invoiceKind,
        });
        if (sendAfter) await api.post(`/admin/invoices/${inv.id}/resend`);
        toast.success(`Rechnung ${inv.invoice_number} aktualisiert: ${euro(r.data.gross)}${sendAfter ? " · per E-Mail versendet" : ""}`);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-3 sm:p-4" onClick={onClose} data-testid="invoice-editor">
      <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-4xl mt-6 mb-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <p className="font-mono-lab text-[10px] tracking-widest text-[#ff0033] uppercase">{isNew ? "Neue Rechnung manuell erstellen" : "Rechnung bearbeiten"}</p>
            <h3 className="font-heading text-2xl uppercase text-white mt-1">{isNew ? "Rechnung" : inv.invoice_number}</h3>
            {isNew && <p className="text-neutral-500 text-xs mt-1">Die Rechnungsnummer wird beim Speichern automatisch aus der zentralen Nummernfolge vergeben.</p>}
          </div>
          <button onClick={onClose} data-testid="invoice-editor-close" className="text-neutral-400 hover:text-white p-1"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Kunde */}
          <div>
            <p className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase mb-2">Rechnungsempfänger</p>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input value={q} onChange={(e) => search(e.target.value)} data-testid="inv-customer-search"
                placeholder="Bestandskunde suchen (Name, E-Mail) – oder Daten unten manuell eintragen"
                className={`${inputCls} pl-9`} />
              {hits.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-[#141414] border border-white/15 max-h-56 overflow-y-auto">
                  {hits.map((h) => (
                    <button key={h.email} onClick={() => pick(h)} data-testid={`inv-customer-hit-${h.email}`}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2">
                      <UserCheck size={14} className="text-[#ff0033] shrink-0" />
                      <span className="text-white text-sm">{h.name || h.email}</span>
                      <span className="text-neutral-500 text-xs truncate">{h.email} {h.city ? `· ${h.city}` : ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block"><span className={lbl}>Vorname</span><input value={c.first_name} onChange={(e) => setC({ ...c, first_name: e.target.value })} className={inputCls} data-testid="inv-first-name" /></label>
              <label className="block"><span className={lbl}>Nachname</span><input value={c.last_name} onChange={(e) => setC({ ...c, last_name: e.target.value })} className={inputCls} data-testid="inv-last-name" /></label>
              <label className="block"><span className={lbl}>Firma (optional)</span><input value={c.company} onChange={(e) => setC({ ...c, company: e.target.value })} className={inputCls} data-testid="inv-company" /></label>
              <label className="block md:col-span-2"><span className={lbl}>Straße & Hausnr.</span><input value={c.street} onChange={(e) => setC({ ...c, street: e.target.value })} className={inputCls} data-testid="inv-street" /></label>
              <label className="block"><span className={lbl}>PLZ</span><input value={c.postcode} onChange={(e) => setC({ ...c, postcode: e.target.value })} className={inputCls} data-testid="inv-postcode" /></label>
              <label className="block"><span className={lbl}>Ort</span><input value={c.city} onChange={(e) => setC({ ...c, city: e.target.value })} className={inputCls} data-testid="inv-city" /></label>
              <label className="block"><span className={lbl}>Land</span><input value={c.country} onChange={(e) => setC({ ...c, country: e.target.value })} className={inputCls} data-testid="inv-country" /></label>
              <label className="block"><span className={lbl}>E-Mail</span><input value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} className={inputCls} data-testid="inv-email" /></label>
              <label className="block"><span className={lbl}>Telefon (optional)</span><input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} className={inputCls} data-testid="inv-phone" /></label>
              <label className="block md:col-span-2"><span className={lbl}>USt-ID (optional)</span><input value={c.vat_id} onChange={(e) => setC({ ...c, vat_id: e.target.value })} className={inputCls} data-testid="inv-vat-id" /></label>
            </div>
          </div>

          {/* Rechnungsart / Steuernummer */}
          <div className={invoiceKind === "shop" ? "border border-[#ff0033]/40 bg-[#ff0033]/[0.06] p-3" : ""}>
            <label className="block">
              <span className={lbl}>Rechnungsart / Steuernummer</span>
              <select value={invoiceKind} onChange={(e) => setInvoiceKind(e.target.value)} className={`${inputCls} mt-1`} data-testid="inv-invoice-kind">
                <option value="golftraining">Golftraining (Standard) · Steuernr. 90464/28232</option>
                <option value="shop">Shop · Steuernr. 90464/28249</option>
              </select>
            </label>
            {invoiceKind === "shop" && (
              <p className="text-[#ff0033] font-heading uppercase tracking-wider text-sm mt-2" data-testid="inv-shop-marker">
                Rechnung Shop – diese Rechnung wird oben rot als „RECHNUNG SHOP" markiert
              </p>
            )}
          </div>

          {/* Daten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block"><span className={lbl}>Rechnungsdatum</span>
              <GermanDatePicker value={issueDate} onChange={setIssueDate} placeholder="Rechnungsdatum" className="p-2.5" testId="inv-issue-date" /></label>
            <label className="block"><span className={lbl}>Leistungsdatum</span>
              <GermanDatePicker value={serviceDate} onChange={setServiceDate} placeholder="Leistungsdatum" className="p-2.5" testId="inv-service-date" /></label>
          </div>

          {/* Positionen */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase">Rechnungspositionen</p>
              <button onClick={addLine} data-testid="inv-add-line" className="flex items-center gap-1.5 border border-white/15 text-neutral-300 hover:border-[#ff0033] hover:text-white font-mono-lab text-[10px] uppercase tracking-widest px-3 py-2"><Plus size={13} /> Position</button>
            </div>
            <div className="space-y-3">
              {lines.map((l, i) => {
                const t = lineTotals(l);
                return (
                  <div key={i} className="bg-[#111] border border-white/10 p-3" data-testid={`inv-line-${i}`}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                      <label className="block md:col-span-6"><span className={lbl}>Beschreibung der Leistung</span>
                        <input value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} className={inputCls} data-testid={`inv-line-name-${i}`} placeholder="z.B. Einzeltraining 60 Minuten" /></label>
                      <label className="block md:col-span-2"><span className={lbl}>Menge</span>
                        <input value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className={inputCls} data-testid={`inv-line-qty-${i}`} /></label>
                      <label className="block md:col-span-2"><span className={`${lbl} whitespace-nowrap`}>Einzelpreis €</span>
                        <input value={l.gross_unit} onChange={(e) => setLine(i, "gross_unit", e.target.value)} className={inputCls} data-testid={`inv-line-unit-${i}`} placeholder="0,00" /></label>
                      <label className="block md:col-span-2"><span className={lbl}>MwSt.</span>
                        <select value={l.vat_rate} onChange={(e) => setLine(i, "vat_rate", e.target.value)} className={inputCls} data-testid={`inv-line-vat-${i}`}>
                          <option value="19">19 %</option><option value="7">7 %</option><option value="0">0 %</option>
                        </select></label>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-2.5">
                      <p className="text-xs text-neutral-400 font-mono-lab" data-testid={`inv-line-totals-${i}`}>
                        Netto <span className="text-neutral-200">{euro(t.net)}</span> · MwSt {t.rate} % <span className="text-neutral-200">{euro(t.vat)}</span> · Brutto <span className="text-white">{euro(t.gross)}</span>
                      </p>
                      {lines.length > 1 && (
                        <button onClick={() => delLine(i)} data-testid={`inv-line-del-${i}`} className="text-neutral-500 hover:text-[#ff0033] flex items-center gap-1 text-xs"><Trash2 size={14} /> entfernen</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex flex-col items-end gap-0.5 text-sm" data-testid="inv-totals">
              <p className="text-neutral-400">Netto: <span className="text-neutral-200 font-mono-lab">{euro(totals.net)}</span></p>
              <p className="text-neutral-400">MwSt.: <span className="text-neutral-200 font-mono-lab">{euro(totals.vat)}</span></p>
              <p className="text-white font-heading text-lg">Gesamt brutto: {euro(totals.gross)}</p>
            </div>
            {hasZeroVat && (
              <label className="block mt-3"><span className={lbl}>Hinweis / Grund für 0 % MwSt. (erscheint auf der Rechnung)</span>
                <input value={vatNote} onChange={(e) => setVatNote(e.target.value)} className={inputCls} data-testid="inv-vat-note"
                  placeholder='z.B. "Steuerschuldnerschaft des Leistungsempfängers / Reverse Charge"' /></label>
            )}
          </div>

          <label className="block"><span className={lbl}>Einleitungstext / Dank (optional, erscheint auf der Rechnung)</span>
            <textarea value={thankYou} onChange={(e) => setThankYou(e.target.value)} rows={2} className={inputCls} data-testid="inv-thank-you" /></label>

          <div className="border-t border-white/10 pt-5">
            <label className="block">
              <span className={lbl}>E-Mail-Begleittext für diese Rechnung (optional)</span>
              <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} rows={6} className={`${inputCls} mt-1`} data-testid="inv-email-message"
                placeholder={"Hallo {name},\n\nanbei erhalten Sie Ihre Rechnung {invoice_number} über {amount_fmt}.\nWir bedanken uns herzlich.\n\nFreundliche Grüße\nGolfcoaching Academy Wendlingen"} /></label>
            <p className="text-neutral-500 text-[11px] mt-1.5 leading-relaxed">
              Dieser Text wird nur als E-Mail-Nachricht <span className="text-neutral-300">zu dieser Rechnung</span> verwendet (die PDF-Rechnung hängt immer im Anhang). Er <span className="text-neutral-300">verändert nicht</span> die Standardvorlage im Bereich „E-Mail-Texte". Wird das Feld leer gelassen, greift der Standardtext.<br />
              Platzhalter: <span className="font-mono-lab text-neutral-400">{"{name}"}</span>, <span className="font-mono-lab text-neutral-400">{"{invoice_number}"}</span>, <span className="font-mono-lab text-neutral-400">{"{amount_fmt}"}</span>, <span className="font-mono-lab text-neutral-400">{"{course}"}</span>, <span className="font-mono-lab text-neutral-400">{"{when}"}</span>
            </p>
          </div>

          <label className="flex items-center gap-2 text-neutral-300 text-sm cursor-pointer">
            <input type="checkbox" checked={sendAfter} onChange={(e) => setSendAfter(e.target.checked)} className="accent-[#ff0033]" data-testid="inv-send-after" />
            Rechnung nach dem Speichern direkt per E-Mail an den Kunden senden
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
          <button onClick={onClose} className="font-mono-lab text-[11px] uppercase tracking-widest text-neutral-400 hover:text-white px-4 py-2.5">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="inv-save" className="bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-2.5 hover:bg-[#cc0029] transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {isNew ? "Rechnung erstellen" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceEditor;
